/**
 * `/login` — the login page (PRD §4.7, §8). No signup. One form for everyone:
 * email + password (Anmelden) and Magic Link by email. The Google/OAuth tab from
 * the reference is banned (PRD §2.2).
 *
 * The server answers a link request identically for known and unknown addresses (PLAN.md D-16), so
 * this page always shows the same confirmation.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GrantonomyLogo } from "@/components/g-logo";
import { LegalContinueNotice } from "@/components/legal-links";
import { PublicLayout } from "@/components/public-layout";
import { RateLimitNotice } from "@/components/rate-limit-notice";
import { TurnstileField, type TurnstileFieldHandle } from "@/components/turnstile-field";
import { api, ApiError } from "@/lib/api";
import { rateLimitedFromError } from "@/lib/rate-limit";
import { homePathFor, SESSION_QUERY_KEY, useSession } from "@/lib/session";
import type { RateLimitedPayload } from "@/shared/errors";
import type { SessionResponse } from "@/shared/schemas/auth";

type LoginTurnstileAction = "login" | "magic-link";

export function AuthPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, isLoading } = useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rateLimit, setRateLimit] = useState<RateLimitedPayload | null>(null);
  const [action, setAction] = useState<LoginTurnstileAction>("login");
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<TurnstileFieldHandle>(null);
  const pendingRef = useRef<LoginTurnstileAction | null>(null);

  function resetTurnstile() {
    widgetRef.current?.reset();
  }

  function onAuthError(error: unknown) {
    const payload = rateLimitedFromError(error);
    if (payload) {
      setRateLimit(payload);
      return;
    }
    toast.error(describe(error, t));
  }

  const login = useMutation({
    mutationFn: (input: { email: string; password: string; token: string }) =>
      api.post<SessionResponse>("/auth/login", {
        email: input.email,
        password: input.password,
        "cf-turnstile-response": input.token,
      }),
    onSuccess: (data) => queryClient.setQueryData(SESSION_QUERY_KEY, data),
    onError: onAuthError,
    onSettled: () => resetTurnstile(),
  });

  const magicLink = useMutation({
    mutationFn: (input: { email: string; token: string }) =>
      api.post("/auth/magic-link", {
        email: input.email,
        "cf-turnstile-response": input.token,
      }),
    onSuccess: () => toast.success(t("auth.magicLinkSent")),
    onError: onAuthError,
    onSettled: () => resetTurnstile(),
  });

  useEffect(() => {
    if (!token || pendingRef.current !== action) return;
    const pending = pendingRef.current;
    pendingRef.current = null;
    const address = email.trim().toLowerCase();
    if (pending === "login") {
      login.mutate({ email: address, password, token });
      return;
    }
    if (!address) {
      toast.error(t("auth.magicLinkNoEmail"));
      return;
    }
    if (pending === "magic-link") magicLink.mutate({ email: address, token });
  }, [token, action, email, password, login, magicLink, t]);

  if (!isLoading && user) return <Navigate to={homePathFor(user)} replace />;

  const busy = login.isPending || magicLink.isPending;
  const loginLocked = rateLimit?.target === "login";
  const emailLocked = rateLimit?.target === "magic_link";

  function begin(kind: LoginTurnstileAction) {
    if (kind !== "login") {
      const address = email.trim().toLowerCase();
      if (!address) {
        toast.error(t("auth.magicLinkNoEmail"));
        return;
      }
    }
    if (action === kind && token) {
      const address = email.trim().toLowerCase();
      if (kind === "login") {
        login.mutate({ email: address, password, token });
        return;
      }
      magicLink.mutate({ email: address, token });
      return;
    }
    pendingRef.current = kind;
    if (action !== kind) {
      setToken(null);
      setAction(kind);
    }
  }

  function submitPassword(event: FormEvent) {
    event.preventDefault();
    begin("login");
  }

  return (
    <PublicLayout>
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <GrantonomyLogo />
          <h1 className="mt-7 font-display text-4xl">{t("auth.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("auth.subtitle")}</p>

          <div className="mt-6 rounded-2xl border border-border bg-card p-8 shadow-soft">
            <form className="space-y-4" onSubmit={submitPassword}>
              {rateLimit && (
                <RateLimitNotice payload={rateLimit} onExpired={() => setRateLimit(null)} />
              )}
              <div>
                <Label className="text-xs" htmlFor="email">
                  {t("common.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("auth.emailPlaceholder")}
                />
              </div>
              <div>
                <Label className="text-xs" htmlFor="password">
                  {t("auth.passwordLabel")}
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <div className="-mb-2 flex justify-end">
                  <Link
                    to={
                      email.trim()
                        ? `/login/forgot?email=${encodeURIComponent(email.trim().toLowerCase())}`
                        : "/login/forgot"
                    }
                    className="inline-flex min-h-6 items-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    {t("auth.forgotPassword")}
                  </Link>
                </div>
              </div>
              <TurnstileField ref={widgetRef} action={action} onToken={setToken} />
              <Button
                type="submit"
                className="w-full"
                disabled={busy || loginLocked || !password || (action === "login" && !token)}
              >
                {login.isPending ? t("auth.passwordSubmitLoading") : t("auth.passwordSubmit")}
              </Button>
            </form>

            <Button
              type="button"
              variant="outline"
              className="mt-3 w-full"
              onClick={() => begin("magic-link")}
              disabled={busy || emailLocked || (action === "magic-link" && !token)}
            >
              {magicLink.isPending ? t("auth.magicLinkLoading") : t("auth.magicLink")}
            </Button>
            <p className="mt-3 text-[11px] text-muted-foreground">{t("auth.magicLinkHelp")}</p>

            <LegalContinueNotice className="mt-4" />
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}

function describe(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) return error.localizedMessage;
  return t("errors.INTERNAL_ERROR");
}
